import React, { useState } from 'react';
import { leaseApi } from '../services/api';
import '../styles/Modal.css';

function UploadLeaseModal({ entity, onClose, onComplete }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        setError('Please select an Excel file (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.name.endsWith('.xlsx')) {
        setError('Please select an Excel file (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const data = await leaseApi.uploadLeaseData(entity.name, selectedFile);
      setResult(data);
      if (onComplete) {
        setTimeout(() => onComplete(), 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload lease data');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Lease Data</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {result && (
            <div className="alert alert-success">
              Successfully uploaded lease data!
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li>Lease Count: {result.summary.lease_count}</li>
                {result.summary.template_version === 2 ? (
                  <li>Lease IDs: {(result.summary.lease_ids || []).join(', ')}</li>
                ) : (
                  <>
                    <li>Total Tranches: {result.summary.tranche_count}</li>
                    <li>Lease Groups: {(result.summary.lease_groups || []).join(', ')}</li>
                  </>
                )}
              </ul>
            </div>
          )}

          {!result && (
            <>
              <p>Upload lease input data for {entity.name}</p>

              {/* File Upload Area */}
              <div
                className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="lease-file-input"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="lease-file-input" className="file-upload-label">
                  {selectedFile ? (
                    <>
                      <div className="file-icon">📄</div>
                      <div className="file-name">{selectedFile.name}</div>
                      <div className="file-size">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="file-icon">📁</div>
                      <div>Click to browse or drag and drop</div>
                      <div className="file-hint">Excel files only (.xlsx)</div>
                    </>
                  )}
                </label>
              </div>

              {selectedFile && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedFile(null)}
                  style={{ marginTop: '10px' }}
                >
                  Clear Selection
                </button>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadLeaseModal;
